import { confirmAuthorization, authorizeWithGoogle } from "@vetty/api-sdk";
import {
  axiosInstance,
  isExactOAuthRedirectUri,
  loginUser,
  validateEmail,
} from "@/helpers";
import { setFieldError, setLoading } from "@/stores";
import { useAuth, useForm } from "@/hooks";
import { useSearchParams } from "react-router-dom";
import { useState } from "react";

/**
 * useAuthorizeForm Custom Hook
 *
 * Manages authorize form state and submission logic.
 * Handles validation, API calls, error handling, and navigation.
 */
export const useAuthorizeForm = () => {
  // Get form state from global store
  const { values, loading } = useForm();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [authSuccess, setAuthSuccess] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const authorizationId = params.get("authorization_id");
  const state = params.get("state");

  /**
   * Redirect using the exact allowlisted URI returned by the API
   * (never trust the query-string redirect_uri alone).
   */
  const redirectToDesktopApp = (code: string, serverRedirectUri?: string) => {
    const raw = String(serverRedirectUri ?? "").trim();
    if (!isExactOAuthRedirectUri(raw)) {
      setLoading(false);
      setAuthError("Invalid redirect URI.");
      return;
    }

    const redirectUrl = new URL(raw);
    redirectUrl.searchParams.set("code", code);
    if (state) {
      redirectUrl.searchParams.set("state", state as string);
    }

    try {
      window.location.href = redirectUrl.toString();

      // If user is still on page after 2 seconds, show success message
      setTimeout(() => {
        setLoading(false);
        setAuthSuccess(true);
      }, 2000);
    } catch (error) {
      console.error("Redirect error:", error);
      setLoading(false);
      setAuthSuccess(true);
    }
  };

  /**
   * Validates authorize form inputs
   * @returns validation result with error flags
   */
  const validateForm = () => {
    const email = String(values.email ?? "");
    const password = String(values.password ?? "");
    const emailError = !validateEmail(email);
    const passwordError = password.length === 0;

    return {
      emailError,
      passwordError,
      isValid: !emailError && !passwordError,
    };
  };

  /**
   * Handles authorize API response
   * @param response - API response object
   */
  const handleAuthorizeResponse = async (response: any) => {
    switch (response.code) {
      case "logged_in":
        try {
          const userId = response.data.id;

          const { data } = await confirmAuthorization(axiosInstance, {
            authorizationId: String(authorizationId ?? ""),
            userId: String(userId ?? ""),
          });

          redirectToDesktopApp(data.data.code, data.data.redirectUri);
        } catch (error) {
          console.error("Authorization error:", error);
          setLoading(false);
          setAuthError("Authorization failed. Please try again.");
        }
        break;

      case "invalid_credentials":
        setFieldError("email", "Invalid credentials");
        setFieldError("password", "Invalid credentials");
        setLoading(false);
        break;

      default:
        setFieldError("password", "Something went wrong!");
        setLoading(false);
        break;
    }
  };

  const submitAuthorize = async () => {
    setLoading(true);
    setAuthError(null);

    try {
      if (!user?.id) {
        setLoading(false);
        setAuthError("You must be signed in to authorize.");
        return;
      }

      const { data } = await confirmAuthorization(axiosInstance, {
        authorizationId: String(authorizationId ?? ""),
        userId: user.id,
      });

      redirectToDesktopApp(data.data.code, data.data.redirectUri);
    } catch (error) {
      console.error("Authorize error:", error);
      setLoading(false);
      setAuthError("Authorization failed. Please try again.");
    }
  };

  /**
   * Submits authorize form with validation and error handling
   */
  const submitLogin = async () => {
    setLoading(true);

    // Validate form inputs
    const validation = validateForm();

    if (!validation.isValid) {
      // Set validation errors
      setFieldError("email", validation.emailError ? "Invalid e-mail" : "");
      setFieldError(
        "password",
        validation.passwordError ? "Password required" : ""
      );
      setLoading(false);
      return;
    }

    try {
      // Attempt user authorize
      const email = String(values.email ?? "");
      const password = String(values.password ?? "");
      const response = await loginUser(email, password);

      // Handle response
      await handleAuthorizeResponse(response);
    } catch (error) {
      // Handle unexpected errors
      console.error("Authorize error:", error);
      setFieldError("password", "Something went wrong!");
      setLoading(false);
    }
  };

  const submitGoogleAuthorize = async (googleCode: string) => {
    setLoading(true);
    setAuthError(null);

    try {
      const { data } = await authorizeWithGoogle(axiosInstance, {
        code: googleCode,
        authorizationId: authorizationId ?? undefined,
      });

      redirectToDesktopApp(data.data.code, data.data.redirectUri);
    } catch (error) {
      console.error("Google authorize error:", error);
      setLoading(false);
      setAuthError("Google authorization failed. Please try again.");
    }
  };

  return {
    submitAuthorize,
    submitLogin,
    submitGoogleAuthorize,
    loading,
    values,
    authSuccess,
    authError,
  };
};
